// src/components/compliance/MyRequirementsTab.tsx
//
// A read-only view of the current user's own compliance eligibility:
// which requirements apply to them, which are satisfied, which are
// missing, and which are expiring. Uses the backend eligibility
// engine — never re-derives status in the browser.

import { useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  useEvaluateComplianceSubjectQuery,
  useGetComplianceDocumentsQuery,
} from '../../api/complianceApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorState from '../common/ErrorState';
import { EligibilityChip, EnforcementModeChip, DocumentStatusChip } from './Chips';
import { formatShortDate, messageFromComplianceError } from './complianceDisplay';

interface MyRequirementsTabProps {
  workspaceId: string;
}

export default function MyRequirementsTab({ workspaceId: _workspaceId }: MyRequirementsTabProps) {
  const { user } = useAuth();

  const {
    data: eligibility,
    isLoading: eLoading,
    error: eError,
    refetch,
  } = useEvaluateComplianceSubjectQuery(
    user?.id
      ? { subjectType: 'provider_user', subjectId: user.id }
      : { subjectType: 'provider_user', subjectId: '' },
    { skip: !user?.id },
  );

  const {
    data: myDocs,
    isLoading: dLoading,
  } = useGetComplianceDocumentsQuery(
    user?.id
      ? { subjectType: 'provider_user', subjectId: user.id, limit: 500 }
      : undefined,
    { skip: !user?.id },
  );

  if (!user?.id) {
    return <ErrorState title="Not signed in" message="Please sign in again to view your requirements." />;
  }
  if (eLoading || dLoading) return <LoadingSpinner />;
  if (eError) return <ErrorState title="Could not evaluate your compliance" message={messageFromComplianceError(eError)} onRetry={refetch} />;
  if (!eligibility) return null;

  return (
    <section className="space-y-4" data-testid="compliance-my-tab">
      <div className="rounded-lg border border-gray-200 bg-white p-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500">Overall status</div>
          <div className="mt-1 flex items-center gap-3">
            <EligibilityChip severity={eligibility.status} />
            <span className="text-sm text-gray-500">
              Evaluated {formatShortDate(eligibility.evaluatedAt)}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wide text-gray-500">Requirements</div>
          <div className="text-sm text-gray-800">{eligibility.requirements.length} total</div>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white">
        <header className="px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Requirements applied to you</h3>
        </header>
        <ul className="divide-y divide-gray-100">
          {eligibility.requirements.length === 0 && (
            <li className="p-4 text-sm text-gray-500">No requirements apply to you.</li>
          )}
          {eligibility.requirements.map((line) => (
            <li key={line.requirementId} className="p-4" data-testid={`compliance-my-req-${line.requirementId}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900">{line.requirementName}</span>
                    <EligibilityChip severity={line.severity} />
                    <EnforcementModeChip mode={line.enforcement} />
                    {line.expiring && !line.expired && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 ring-1 ring-amber-200">
                        Expiring soon
                      </span>
                    )}
                    {line.expired && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 ring-1 ring-red-300">
                        Expired
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-600">{line.reason}</p>
                  {line.expiresAt && (
                    <p className="mt-1 text-xs text-gray-500">
                      Expires {formatShortDate(line.expiresAt)}
                    </p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        <header className="px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Your documents</h3>
        </header>
        {(myDocs?.rows ?? []).length === 0 ? (
          <div className="p-4 text-sm text-gray-500">You don't have any compliance documents yet.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {(myDocs?.rows ?? []).map((doc) => (
              <li key={doc.id} className="p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`compliance-my-doc-${doc.id}`}>
                <div>
                  <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                  <div className="text-xs text-gray-500">
                    {doc.requirement?.name ?? '—'} · Expires {formatShortDate(doc.expiresAt)}
                  </div>
                </div>
                <DocumentStatusChip status={doc.status} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
